import React from "react";
import { BoardPlaneIcon, LIGHT } from "./parts";


const buildStyles = (cfg, t) => {
  const dual = !!cfg._dual;
  const dense = dual || !!cfg._compact;
  const dualRow = (cfg.dualDir || "column") === "row";
  const flightGap = dense ? "0.45vw" : "0.7vw";
  return {
    wrap: {
      height: "100%",
      width: "100%",
      boxSizing: "border-box",
      background: t.wrapBg,
      color: t.text,
      fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
      padding: dual ? cfg.dualPad || cfg.pad : cfg.pad,
      display: "flex",
      flexDirection: "column",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexShrink: 0,
      marginBottom: dual ? "1vh" : "0.8vh",
      width: "100%",
    },
    headLeft: {
      display: "flex",
      alignItems: "center",
      minWidth: 0,
      flex: 1,
      marginRight: "1.5vw",
    },
    badge: {
      background: t.iconBoxBg,
      borderRadius: "0.9vh",
      width: dual ? cfg.dualIconBox || "5.2vh" : cfg.iconBox,
      height: dual ? cfg.dualIconBox || "5.2vh" : cfg.iconBox,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      marginRight: "1.2vw",
    },
    title: {
      fontSize: dual ? cfg.dualTitleSize || cfg.titleSize : cfg.titleSize,
      fontWeight: 800,
      color: t.title,
      lineHeight: 1.05,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    },
    airport: {
      fontSize: dual ? cfg.dualAirportSize || cfg.airportSize : cfg.airportSize,
      color: t.subtitleColor,
      marginTop: "0.35vh",
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      fontWeight: 600,
    },
    headRight: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      justifyContent: "center",
      flexShrink: 0,
    },
    date: {
      fontSize: dual ? cfg.dualClockSize || cfg.clockSize : cfg.clockSize,
      fontWeight: 700,
      color: t.dateColor,
      letterSpacing: "0.04em",
      whiteSpace: "nowrap",
      marginBottom: "0.4vh",
    },
    clock: {
      fontSize:
        "calc(" +
        (dense ? cfg.dualClockSize || cfg.clockSize : cfg.clockSize) +
        " * 1.9)",
      fontWeight: 800,
      color: t.timeColor,
      letterSpacing: "0.02em",
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1,
      whiteSpace: "nowrap",
      marginBottom: "0.6vh",
    },
    dots: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: t.borderStrong,
      marginLeft: "0.5vw",
    },
    dotFirst: { marginLeft: 0 },
    dotActive: { background: t.accent },
    colHead: {
      display: "grid",
      gridTemplateColumns: cfg.cols,
      width: "100%",
      color: t.headColor,
      fontSize: dense
        ? cfg.dualHeadSize || cfg.headSize
        : cfg.headSize,
      fontWeight: 700,
      letterSpacing: dense ? "0.04em" : "0.08em",
      textTransform: "uppercase",
      padding: dense ? "0.8vh 0 0.7vh" : "1.2vh 0 1vh",
      flexShrink: 0,
      boxSizing: "border-box",
      gridColumnGap: dense ? "0.4vw" : "0.3vw",
    },
    body: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      width: "100%",
    },
    row: {
      display: "grid",
      gridTemplateColumns: cfg.cols,
      width: "100%",
      alignItems: "center",
      flex: "0 0 auto",
      minHeight: dense ? cfg.dualRowMin || cfg.rowMin || "3.2vh" : "auto",
      flexGrow: 1,
      flexShrink: 1,
      borderTop: "1px solid " + t.border,
      fontSize: dense
        ? cfg.dualRowSize || cfg.rowSize
        : cfg.rowSize,
      boxSizing: "border-box",
      gridColumnGap: dense ? "0.4vw" : "0.3vw",
      padding: dense ? "0.55vh 0" : "0",
    },
    time: {
      color: t.timeColor,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    est: {
      color: t.estColor,
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    timeStack: {
      display: "flex",
      flexDirection: "column",
      lineHeight: 1.15,
      minWidth: 0,
    },
    flightCell: {
      display: "flex",
      alignItems: "center",
      minWidth: 0,
      overflow: "hidden",
    },
    airlineBadge: {
      width: dense ? cfg.dualBadgeSize || cfg.badgeSize : cfg.badgeSize,
      height: dense ? cfg.dualBadgeSize || cfg.badgeSize : cfg.badgeSize,
      borderRadius: "0.4vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      fontWeight: 800,
      fontSize: dense
        ? cfg.dualBadgeFont || cfg.badgeFont
        : cfg.badgeFont,
      flexShrink: 0,
      marginRight: flightGap,
    },
    flightNo: {
      color: t.title,
      fontWeight: 700,
      minWidth: 0,
      // Wrap onto a second line instead of "…" (break the number if needed).
      whiteSpace: "normal",
      overflowWrap: "anywhere",
      lineHeight: 1.1,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    toCell: {
      minWidth: 0,
      overflow: "hidden",
      display: "flex",
      // Center the code against the name: same line when 1 line, vertically
      // centered against a 2-line name.
      alignItems: "center",
      whiteSpace: "normal",
      paddingRight: "0.6vw",
    },
    toName: {
      color: t.title,
      fontWeight: 600,
      minWidth: 0,
      flex: 1,
      whiteSpace: "normal",
      lineHeight: 1.1,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    toCode: {
      color: t.codeColor,
      fontWeight: 600,
      flexShrink: 0,
      marginRight: "0.4vw",
    },
    term: {
      color: t.title,
      fontWeight: 600,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      // Terminal value is always centered in its column.
      textAlign: "center",
    },
    termHead: {
      textAlign: "center",
    },
    status: {
      fontSize:
        "calc(" +
        (dense ? cfg.dualRowSize || cfg.rowSize : cfg.rowSize) +
        " * 0.92)",
      fontWeight: 800,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
      textAlign: "left",
      minWidth: 0,
      paddingLeft: "0.4vw",
      // Wrap onto a second line instead of "…".
      whiteSpace: "normal",
      lineHeight: 1.1,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    },
    statusHead: {
      textAlign: "left",
      paddingLeft: "0.4vw",
    },
    empty: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: t.textMuted,
      fontSize: dense
        ? cfg.dualRowSize || cfg.rowSize
        : cfg.rowSize,
    },
    sections: Object.assign(
      {
        flex: 1,
        display: "flex",
        flexDirection: dualRow ? "row" : "column",
        minHeight: 0,
        // No fixed width here: with `width:100%` the negative side margins
        // below would just shift the row left (leaving extra space on the
        // right) instead of widening it symmetrically. Letting it stretch
        // keeps both outer edges equal.
        alignSelf: "stretch",
      },
      dualRow
        ? { marginLeft: "-0.7vw", marginRight: "-0.7vw" }
        : { marginTop: "-0.8vh", marginBottom: "-0.8vh" },
    ),
    section: Object.assign(
      {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        width: dualRow ? "50%" : "100%",
        minWidth: 0,
        overflow: "hidden",
        border: "1px solid " + t.border,
        borderRadius: "1.2vh",
        padding: dual
          ? dualRow
            ? "1.2vh 1vw"
            : "1.2vh 1.2vw"
          : 0,
        boxSizing: "border-box",
        background: t.panelBg,
        boxShadow: t.shadow,
      },
      dualRow
        ? { marginLeft: "0.7vw", marginRight: 0 }
        : { marginTop: "0.8vh", marginBottom: "0.8vh" },
    ),
    sectionHead: {
      display: "flex",
      alignItems: "center",
      marginBottom: "0.8vh",
      flexShrink: 0,
      borderBottom: "1px solid " + t.border,
      paddingBottom: "0.7vh",
    },
    sectionBadge: {
      background: t.iconBoxBg,
      borderRadius: "0.45vh",
      width: cfg.sectionIconBox || "3vh",
      height: cfg.sectionIconBox || "3vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      marginRight: "0.7vw",
    },
    sectionTitle: {
      fontSize: cfg.sectionTitleSize || "1.8vh",
      fontWeight: 800,
      letterSpacing: "0.1em",
      color: t.accent,
    },
  };
};

const PageDots = ({ page, pages, s }) => {
  if (!pages || pages <= 1) return null;
  const maxDots = Math.min(pages, 6);
  return (
    <div style={s.dots}>
      {Array.from({ length: maxDots }).map(function (_, i) {
        return (
          <span
            key={i}
            style={Object.assign(
              {},
              s.dot,
              i === 0 ? s.dotFirst : null,
              i === page % maxDots ? s.dotActive : null,
            )}
          />
        );
      })}
    </div>
  );
};

// full single-board layout
const FullRows = ({ rows, s, emptyLabel }) =>
  rows.length ? (
    <div style={s.body}>
      {rows.map((r, i) => (
        <div style={s.row} key={`${r.flight}-${i}`}>
          <span style={s.time}>{r.time}</span>
          <span style={s.est}>{r.est}</span>
          <span style={s.flightCell}>
            <span style={s.flightNo}>{r.flight}</span>
          </span>
          <span style={s.toCell} title={r.toTitle}>
            <span style={s.toName}>{r.toName}</span>
            {r.toCode ? <span style={s.toCode}>({r.toCode})</span> : null}
          </span>
          <span style={s.term}>{r.terminal}</span>
          <span
            style={Object.assign({}, s.status, { color: r.status.color })}
          >
            {r.status.label}
          </span>
        </div>
      ))}
    </div>
  ) : (
    <div style={s.empty}>{emptyLabel}</div>
  );

const FullHead = ({ isDep, s }) => (
  <div style={s.colHead}>
    <span>TIME</span>
    <span>{isDep ? "EST. DEP" : "EST. ARR"}</span>
    <span>FLIGHT</span>
    <span>{isDep ? "TO" : "FROM"}</span>
    <span style={s.termHead}>TERMINAL</span>
    <span style={s.statusHead}>STATUS</span>
  </div>
);

// compact dual-panel layout
const DualRows = ({ rows, s, emptyLabel }) =>
  rows.length ? (
    <div style={s.body}>
      {rows.map((r, i) => (
        <div style={s.row} key={`${r.flight}-${i}`}>
          <span style={s.timeStack}>
            <span style={s.time}>{r.time}</span>
            {r.est && r.est !== r.time ? (
              <span style={s.est}>{r.est}</span>
            ) : null}
          </span>
          <span style={s.flightCell}>
            <span style={s.flightNo}>{r.flight}</span>
          </span>
          <span
            style={s.toCell}
            title={r.toTitle || `${r.toName} ${r.toCode || ""}`}
          >
            {r.toCode ? (
              <>
                <span style={s.toCode}>{r.toCode}</span>
                <span style={s.toName}>{r.toName}</span>
              </>
            ) : (
              <span style={s.toName}>{r.toName}</span>
            )}
          </span>
          <span style={s.term}>{r.terminal}</span>
          <span
            style={Object.assign({}, s.status, { color: r.status.color })}
          >
            {r.status.label}
          </span>
        </div>
      ))}
    </div>
  ) : (
    <div style={s.empty}>{emptyLabel}</div>
  );

const DualHead = ({ isDep, s }) => (
  <div style={s.colHead}>
    <span>TIME</span>
    <span>FLIGHT</span>
    <span>{isDep ? "TO" : "FROM"}</span>
    <span style={s.termHead}>TERMINAL</span>
    <span style={s.statusHead}>STATUS</span>
  </div>
);

const Panel = ({ isDep, rows, s, cfg, iconFill }) => (
  <div style={s.section}>
    <div style={s.sectionHead}>
      <span style={s.sectionBadge}>
        <BoardPlaneIcon
          size={cfg.sectionIconSize || "2vh"}
          isDep={isDep}
          fill={iconFill}
        />
      </span>
      <div style={s.sectionTitle}>
        {isDep ? "DEPARTURES" : "ARRIVALS"}
      </div>
    </div>
    <DualHead isDep={isDep} s={s} />
    <DualRows
      rows={rows}
      s={s}
      emptyLabel={`No ${isDep ? "departure" : "arrival"} data`}
    />
  </div>
);

const Board = ({
  rows,
  isDep,
  airportLabel,
  dateLabel,
  timeLabel,
  cfg,
  theme,
  depRows,
  arrRows,
  showBoth,
  page = 0,
  pages = 1,
}) => {
  const t = theme || LIGHT;
  const dual = showBoth && Array.isArray(depRows) && Array.isArray(arrRows);
  // portrait single: 5-col compact — 7-col overflows narrow width
  const compact = !dual && !!cfg.compact;

  const activeCfg = dual
    ? Object.assign({}, cfg, {
        _dual: true,
        cols: cfg.dualCols || "12% 20% 36% 14% 18%",
        dualDir: cfg.dualDir || "row",
      })
    : Object.assign(
        {},
        cfg,
        {
          _dual: false,
          _compact: compact,
          cols: cfg.cols,
        },
        compact
          ? {
              dualBadgeSize: cfg.badgeSize,
              dualBadgeFont: cfg.badgeFont,
              dualRowSize: cfg.rowSize,
              dualHeadSize: cfg.headSize,
              dualRowMin: cfg.rowMin || "5vh",
            }
          : null,
      );

  const s = buildStyles(activeCfg, t);
  const iconSz = dual ? cfg.dualIconSize || "3.2vh" : cfg.iconSize;
  const emptyLabel = `No ${isDep ? "departure" : "arrival"} data available.`;

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div style={s.headLeft}>
          <span style={s.badge}>
            <BoardPlaneIcon
              size={iconSz}
              isDep={isDep}
              dual={dual || compact}
              fill={t.iconFill}
            />
          </span>
          <div>
            <div style={s.title}>
              {dual
                ? "FLIGHT INFORMATION"
                : isDep
                ? "DEPARTURES"
                : "ARRIVALS"}
            </div>
            <div style={s.airport}>{airportLabel}</div>
          </div>
        </div>
        <div style={s.headRight}>
          <span style={s.date}>{dateLabel}</span>
          {timeLabel ? <span style={s.clock}>{timeLabel}</span> : null}
          <PageDots page={page} pages={pages} s={s} />
        </div>
      </div>

      {dual ? (
        <div style={s.sections}>
          <Panel isDep rows={depRows} s={s} cfg={cfg} iconFill={t.iconFill} />
          <Panel
            isDep={false}
            rows={arrRows}
            s={s}
            cfg={cfg}
            iconFill={t.iconFill}
          />
        </div>
      ) : compact ? (
        <>
          <DualHead isDep={isDep} s={s} />
          <DualRows rows={rows} s={s} emptyLabel={emptyLabel} />
        </>
      ) : (
        <>
          <FullHead isDep={isDep} s={s} />
          <FullRows rows={rows} s={s} emptyLabel={emptyLabel} />
        </>
      )}
    </div>
  );
};

export default Board;
